import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import GenericFormDrawer from '../../components/GenericFormDrawer';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import Button from '../../components/ui/Button';
import { competencyAsync } from '../performance-settings/config';
import { usePositionCompetenciesApi } from './config';

// Faz E (spec 2026-08-08 §4, E4) — pozisyon detayının "Yetkinlikler" bölümü:
// satır editörü (yetkinlik + hedef seviye 1-5), Kaydet = PUT tam-liste
// (task-3-report.md §1: GET/PUT v1/positions/{id}/competencies). PositionsPage.js
// zaten GenericFormDrawer'ı pozisyonun kendi alanları (code/departman/unvan)
// için kullanıyor — bu bölüm AYRI bir uca (farklı endpoint) yazdığı için AYRI
// bir GenericFormDrawer/Kaydet aksiyonu (FeedbackFormsTab.js'in Task 4'te
// kurduğu schema=[]+children deseniyle AYNI gerekçe: satır editörü şema-tabanlı
// alanlarla ifade edilemiyor). Satır görsel dili PeriodWizard.js'in (Task 4)
// çoklu-form satır editörüyle AYNI (FormRowCard/RemoveRowBtn — burada
// RowCard/RemoveRowBtn).

const RowsSection = styled.div`
  margin-bottom: 4px;
`;

const RowCard = styled.div`
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 10px;
  background: #FAFBFC;
`;

const RowHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  > *:first-child { flex: 1; min-width: 0; }
`;

const LevelWrap = styled.div`
  width: 110px;
  flex-shrink: 0;
`;

const RemoveRowBtn = styled.button`
  flex-shrink: 0;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-top: 6px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  color: #667;
  &:hover { border-color: #E53935; color: #E53935; }
`;

const EmptyText = styled.p`
  color: #A0A8B4;
  font-size: 13px;
  padding: 8px 0 16px;
`;

const LEVEL_OPTIONS = [1, 2, 3, 4, 5].map((l) => ({ id: l, name: String(l) }));

let rowKeySeq = 0;
const nextRowKey = () => `position-competency-row-${Date.now()}-${rowKeySeq += 1}`;

export default function PositionCompetenciesDrawer({ open, onClose, position }) {
  const { t } = useTranslation();
  const { competencies, fetching, save } = usePositionCompetenciesApi(position?.id);
  const [rows, setRows] = useState([]);

  // Drawer açıldığında/GET sonucu değişince satırları hydrate eder — yerel
  // `_key` BE'ye hiç gönderilmez (PeriodWizard.js'in feedbackForms deseniyle
  // AYNI, task-4-report.md §2).
  useEffect(() => {
    if (!open) return;
    setRows((competencies || []).map((c) => ({
      _key: nextRowKey(),
      competencyId: c.competencyId,
      targetLevel: c.targetLevel,
    })));
  }, [open, competencies]);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, { _key: nextRowKey(), competencyId: null, targetLevel: 3 }]);
  }, []);

  const removeRow = useCallback((key) => {
    setRows((prev) => prev.filter((r) => r._key !== key));
  }, []);

  const updateRow = useCallback((key, patch) => {
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  }, []);

  // Kaydet = PUT tam-liste (task-3-report.md §1); henüz yetkinlik seçilmemiş
  // satırlar (competencyId null) savunmacı olarak filtrelenir — BE zaten
  // TargetLevel/CompetencyId'yi atomik doğruluyor.
  const handleSave = useCallback(async () => {
    const payload = rows.filter((r) => r.competencyId != null);
    return save(payload);
  }, [rows, save]);

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={position ? t('positionCompetencies.drawerTitle', { code: position.code }) : ''}
      schema={[]}
      onSave={handleSave}
    >
      {fetching ? (
        <EmptyText>{t('common.loading')}</EmptyText>
      ) : (
        <RowsSection>
          {rows.length === 0 && <EmptyText>{t('positionCompetencies.emptyHint')}</EmptyText>}
          {rows.map((row) => (
            <RowCard key={row._key}>
              <RowHeader>
                <AsyncGenericSelect
                  label={t('positionCompetencies.competencyLabel')}
                  value={row.competencyId}
                  onChange={(v) => updateRow(row._key, { competencyId: v })}
                  onSearch={competencyAsync.onSearch}
                  onGetById={competencyAsync.onGetById}
                />
                <LevelWrap>
                  <GenericSelect
                    label={t('positionCompetencies.targetLevelLabel')}
                    options={LEVEL_OPTIONS}
                    value={row.targetLevel}
                    onChange={(v) => updateRow(row._key, { targetLevel: v })}
                    idKey="id"
                    labelKey="name"
                  />
                </LevelWrap>
                <RemoveRowBtn
                  type="button"
                  onClick={() => removeRow(row._key)}
                  title={t('positionCompetencies.removeRow')}
                >
                  <X size={14} />
                </RemoveRowBtn>
              </RowHeader>
            </RowCard>
          ))}
          <Button type="button" variant="outline" onClick={addRow}>
            + {t('positionCompetencies.addRow')}
          </Button>
        </RowsSection>
      )}
    </GenericFormDrawer>
  );
}
